'use client';

import { useState } from 'react';
import { Box, Button, Collapse, Divider, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import dayjs from 'dayjs';
import type { IDeliverableRound } from '../../redux/buzzbridge/types';
import { DeliverableList } from './DeliverableList';
import { CustomDatePicker } from '../DatePicker/CustomDatePicker';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    BRAND_LUXE_MUTED,
    BRAND_LUXE_PRIMARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../../pages/BrandDashboard/brandDashboardTokens';
import { ugcBudgetDatePickerSx } from '../../pages/BrandDashboard/ugcCampaignFormHelpers';

interface Props {
    rounds: IDeliverableRound[];
    onRoundsChange: (rounds: IDeliverableRound[]) => void;
    selectedPlatforms?: string[];
    campaignType?: string;
    /** Campaign start_date — used as min for round delivery dates */
    startDate?: string;
    /** Campaign end_date — used as max for round delivery dates */
    endDate?: string;
}

const UNIT_OPTIONS = [
    { label: 'days', value: 'day' as const },
    { label: 'weeks', value: 'week' as const },
    { label: 'months', value: 'month' as const },
];

const PLATFORM_DEFAULT_DELIVERABLE: Record<string, { type: string; platform: string; label: string }> = {
    instagram: { type: 'instagram_post', platform: 'instagram', label: 'Instagram Post' },
    tiktok: { type: 'tiktok_video', platform: 'tiktok', label: 'TikTok Video' },
    youtube: { type: 'youtube_video', platform: 'youtube', label: 'YouTube Video' },
    twitter: { type: 'twitter_post', platform: 'twitter', label: 'Twitter/X Post' },
    x: { type: 'twitter_post', platform: 'x', label: 'Twitter/X Post' },
    facebook: { type: 'facebook_post', platform: 'facebook', label: 'Facebook Post' },
};

function defaultDeliverablesForPlatforms(platforms: string[]): any[] {
    return platforms
        .map(p => PLATFORM_DEFAULT_DELIVERABLE[p])
        .filter(Boolean)
        .map(d => ({ type: d.type, platform: d.platform, count: 1, duration_range: 'n/a' }));
}

const PAGE_SIZE = 4;

const scheduleFieldSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
        borderRadius: '8px',
        color: '#fff',
        '& fieldset': { borderColor: BRAND_OUTLINE_VARIANT },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.32)' },
        '&.Mui-focused fieldset': { borderColor: BRAND_LUXE_PRIMARY },
    },
    '& .MuiInputBase-input': { color: '#fff', py: 1.15, fontSize: 13 },
    '& .MuiSelect-icon': { color: '#fff' },
};

function creatorScheduleLine(every: number | '', unit: 'day' | 'week' | 'month', count: number | '') {
    const n = every === '' || Number(every) < 1 ? 1 : Number(every);
    const times = count === '' ? 0 : Number(count);
    const interval = n === 1 ? unit : `${n} ${unit}s`;
    const timesLabel = times === 1 ? '1 time' : `${times || '—'} times`;
    return `Creator will deliver 1 content every ${interval}, ${timesLabel}.`;
}

export function DeliverableRoundBuilder({
    rounds,
    onRoundsChange,
    selectedPlatforms = [],
    campaignType,
    startDate,
    endDate,
}: Props) {
    const [genOpen, setGenOpen] = useState(rounds.length === 0);
    const [genWarning, setGenWarning] = useState<string | null>(null);
    const [genStart, setGenStart] = useState(() =>
        startDate && dayjs(startDate).isValid()
            ? dayjs(startDate).add(7, 'day').toISOString()
            : dayjs().add(8, 'day').toISOString(),
    );
    const [genEvery, setGenEvery] = useState<number | ''>(1);
    const [genUnit, setGenUnit] = useState<'day' | 'week' | 'month'>('week');
    const [genCount, setGenCount] = useState<number | ''>(4);
    const [page, setPage] = useState(0);

    const campaignMin = startDate && dayjs(startDate).isValid() ? startDate : undefined;
    const campaignMax =
        endDate && dayjs(endDate).isValid() ? dayjs(endDate).subtract(1, 'day').toISOString() : undefined;

    function generateRounds() {
        const base = dayjs(genStart);
        if (!base.isValid()) return;
        const everyVal = Math.max(1, Number(genEvery) || 1);
        const count = Math.max(1, Math.min(52, Number(genCount) || 1));
        const fallback = defaultDeliverablesForPlatforms(selectedPlatforms);
        const maxDay = campaignMax ? dayjs(campaignMax) : null;
        const all: IDeliverableRound[] = Array.from({ length: count }, (_, i) => ({
            round_number: i + 1,
            label: '',
            delivery_date: base.add(i * everyVal, genUnit).toISOString(),
            deliverables: rounds[i]?.deliverables?.length ? rounds[i].deliverables : fallback,
        }));
        const valid = maxDay ? all.filter(r => !dayjs(r.delivery_date).isAfter(maxDay)) : all;
        const dropped = all.length - valid.length;
        if (dropped > 0) {
            setGenWarning(
                `${dropped} ${
                    dropped === 1 ? 'delivery was' : 'deliveries were'
                } removed because their dates fall after the campaign end date.`,
            );
        } else {
            setGenWarning(null);
        }
        const renumbered = valid.map((r, i) => ({ ...r, round_number: i + 1 }));
        onRoundsChange(renumbered);
        setGenOpen(false);
        setPage(0);
    }

    function addRound() {
        const lastDate =
            rounds.length > 0 ? dayjs(rounds[rounds.length - 1].delivery_date).add(1, 'week') : dayjs().add(8, 'day');
        onRoundsChange([
            ...rounds,
            {
                round_number: rounds.length + 1,
                label: '',
                delivery_date: lastDate.toISOString(),
                deliverables: defaultDeliverablesForPlatforms(selectedPlatforms),
            },
        ]);
    }

    function updateRound(index: number, patch: Partial<IDeliverableRound>) {
        onRoundsChange(rounds.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    }

    function removeRound(index: number) {
        onRoundsChange(rounds.filter((_, i) => i !== index).map((r, i) => ({ ...r, round_number: i + 1 })));
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Auto-generate panel */}
            <Box sx={{ border: '1px solid rgba(255,201,101,0.2)', borderRadius: 2, overflow: 'hidden' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1.5,
                        cursor: 'pointer',
                        bgcolor: 'rgba(255,201,101,0.06)',
                    }}
                    onClick={() => setGenOpen(v => !v)}>
                    <Typography
                        sx={{
                            fontSize: 12,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: BRAND_LUXE_PRIMARY,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                        }}>
                        Auto-generate schedule
                    </Typography>
                    {genOpen ? (
                        <ExpandLessIcon sx={{ fontSize: 18, color: BRAND_LUXE_PRIMARY }} />
                    ) : (
                        <ExpandMoreIcon sx={{ fontSize: 18, color: BRAND_LUXE_PRIMARY }} />
                    )}
                </Box>
                <Collapse in={genOpen}>
                    <Box sx={{ px: 2, pb: 2.5, pt: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1.6fr) 96px 148px 120px' },
                                gap: 1.5,
                                alignItems: 'end',
                            }}>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        color: BRAND_LUXE_MUTED,
                                        mb: 0.75,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    First delivery date
                                </Typography>
                                <CustomDatePicker
                                    value={genStart || undefined}
                                    onChange={val => setGenStart(val || '')}
                                    minDate={campaignMin}
                                    maxDate={campaignMax}
                                    withTime={false}
                                    withTimezone={false}
                                    sx={ugcBudgetDatePickerSx}
                                />
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        color: BRAND_LUXE_MUTED,
                                        mb: 0.75,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Frequency
                                </Typography>
                                <TextField
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={genEvery}
                                    onChange={e => {
                                        const raw = e.target.value;
                                        setGenEvery(raw === '' ? '' : Math.max(1, parseInt(raw, 10) || 1));
                                    }}
                                    onBlur={() => setGenEvery(v => (v === '' || Number(v) < 1 ? 1 : v))}
                                    inputProps={{ min: 1, max: 365 }}
                                    sx={scheduleFieldSx}
                                />
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        color: BRAND_LUXE_MUTED,
                                        mb: 0.75,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Per
                                </Typography>
                                <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    value={genUnit}
                                    onChange={e => setGenUnit(e.target.value as 'day' | 'week' | 'month')}
                                    sx={scheduleFieldSx}>
                                    {UNIT_OPTIONS.map(o => (
                                        <MenuItem key={o.value} value={o.value}>
                                            {o.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        color: BRAND_LUXE_MUTED,
                                        mb: 0.75,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Deliveries
                                </Typography>
                                <TextField
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={genCount}
                                    onChange={e => {
                                        const raw = e.target.value;
                                        setGenCount(
                                            raw === '' ? '' : Math.max(1, Math.min(52, parseInt(raw, 10) || 1)),
                                        );
                                    }}
                                    onBlur={() => setGenCount(v => (v === '' || Number(v) < 1 ? 1 : v))}
                                    inputProps={{ min: 1, max: 52 }}
                                    sx={scheduleFieldSx}
                                />
                            </Box>
                        </Box>
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: 'rgba(255,255,255,0.72)',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                lineHeight: 1.5,
                            }}>
                            {creatorScheduleLine(genEvery, genUnit, genCount)}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={generateRounds}
                            sx={{
                                alignSelf: 'flex-start',
                                borderColor: BRAND_LUXE_PRIMARY,
                                color: BRAND_LUXE_PRIMARY,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                textTransform: 'none',
                                fontSize: 12,
                                '&:hover': { bgcolor: 'rgba(255,201,101,0.08)' },
                            }}>
                            Generate {genCount || '—'} {genCount !== 1 ? 'deliveries' : 'delivery'}
                        </Button>
                        {genWarning && (
                            <Typography sx={{ fontSize: 12, color: '#f97316', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                ⚠ {genWarning}
                            </Typography>
                        )}
                    </Box>
                </Collapse>
            </Box>

            {/* Delivery list header with Clear all */}
            {rounds.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 12, color: BRAND_LUXE_MUTED, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                        {rounds.length} {rounds.length === 1 ? 'delivery' : 'deliveries'} scheduled
                    </Typography>
                    <Button
                        startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                        onClick={() => {
                            onRoundsChange([]);
                            setPage(0);
                        }}
                        sx={{
                            color: 'rgba(255,255,255,0.35)',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            textTransform: 'none',
                            fontSize: 12,
                            px: 0,
                            minWidth: 0,
                            '&:hover': { color: '#f87171' },
                        }}>
                        Clear all
                    </Button>
                </Box>
            )}

            {/* Paginated delivery list */}
            {rounds.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((round, i) => {
                const index = page * PAGE_SIZE + i;
                const dateExceedsEnd =
                    campaignMax && round.delivery_date ? dayjs(round.delivery_date).isAfter(dayjs(campaignMax)) : false;
                return (
                    <RoundCard
                        key={index}
                        round={round}
                        campaignMin={campaignMin}
                        campaignMax={campaignMax}
                        selectedPlatforms={selectedPlatforms}
                        campaignType={campaignType}
                        onUpdate={patch => updateRound(index, patch)}
                        onRemove={() => removeRound(index)}
                        canRemove={rounds.length > 1}
                        dateError={dateExceedsEnd ? 'This date is after the campaign end date.' : undefined}
                    />
                );
            })}

            {/* Pagination controls */}
            {rounds.length > PAGE_SIZE && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5 }}>
                    <Button
                        size="small"
                        disabled={page === 0}
                        onClick={() => setPage(p => p - 1)}
                        sx={{
                            color: BRAND_LUXE_PRIMARY,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            textTransform: 'none',
                            fontSize: 12,
                            px: 0,
                            '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)' },
                        }}>
                        ← Previous
                    </Button>
                    <Typography sx={{ fontSize: 12, color: BRAND_LUXE_MUTED, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                        {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, rounds.length)} of {rounds.length}
                    </Typography>
                    <Button
                        size="small"
                        disabled={(page + 1) * PAGE_SIZE >= rounds.length}
                        onClick={() => setPage(p => p + 1)}
                        sx={{
                            color: BRAND_LUXE_PRIMARY,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            textTransform: 'none',
                            fontSize: 12,
                            px: 0,
                            '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)' },
                        }}>
                        Next →
                    </Button>
                </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                    startIcon={<AddIcon />}
                    onClick={() => {
                        addRound();
                        setPage(Math.floor(rounds.length / PAGE_SIZE));
                    }}
                    sx={{
                        color: BRAND_LUXE_PRIMARY,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'none',
                        fontSize: 13,
                        px: 0,
                    }}>
                    Add delivery manually
                </Button>
            </Box>
        </Box>
    );
}

function RoundCard({
    round,
    campaignMin,
    campaignMax,
    selectedPlatforms,
    campaignType,
    onUpdate,
    onRemove,
    canRemove,
    dateError,
}: {
    round: IDeliverableRound;
    campaignMin?: string;
    campaignMax?: string;
    selectedPlatforms: string[];
    campaignType?: string;
    onUpdate: (patch: Partial<IDeliverableRound>) => void;
    onRemove: () => void;
    canRemove: boolean;
    dateError?: string;
}) {
    return (
        <Box
            sx={{
                border: `1px solid ${dateError ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: 2,
                p: 2.5,
                bgcolor: BRAND_SURFACE_CONTAINER_LOW,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff' }}>
                    Delivery {round.round_number}
                </Typography>
                {canRemove && (
                    <IconButton
                        onClick={onRemove}
                        size="small"
                        sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#f87171' } }}>
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <Box>
                    <Typography
                        sx={{ fontSize: 11, color: BRAND_LUXE_MUTED, mb: 0.75, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                        Delivery date <span style={{ color: '#E78410' }}>*</span>
                    </Typography>
                    <CustomDatePicker
                        value={round.delivery_date || undefined}
                        onChange={val => onUpdate({ delivery_date: val || '' })}
                        minDate={campaignMin}
                        maxDate={campaignMax}
                        withTime={false}
                        withTimezone={false}
                        sx={ugcBudgetDatePickerSx}
                    />
                    {dateError && (
                        <Typography sx={{ fontSize: 11, color: '#f87171', mt: 0.5, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            {dateError}
                        </Typography>
                    )}
                </Box>
                <Box>
                    <Typography
                        sx={{ fontSize: 11, color: BRAND_LUXE_MUTED, mb: 0.75, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                        Label{' '}
                        <Typography
                            component="span"
                            sx={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            (optional)
                        </Typography>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={`e.g. Delivery ${round.round_number}`}
                        value={round.label || ''}
                        onChange={e => onUpdate({ label: e.target.value })}
                        sx={{ '& .MuiInputBase-input': { color: '#fff', fontSize: 13, py: 1 } }}
                    />
                </Box>
            </Box>

            <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.08)' }} />

            <Typography
                sx={{
                    fontSize: 11,
                    color: BRAND_LUXE_MUTED,
                    mb: 1.5,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                }}>
                Content required for this delivery
            </Typography>
            <DeliverableList
                items={round.deliverables}
                onItemsChange={deliverables => onUpdate({ deliverables })}
                selectedPlatforms={selectedPlatforms}
                campaignType={campaignType as any}
            />
        </Box>
    );
}
